import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { readPage, readItem } from '../../../utils/apiResult';

const useSuccessionApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  // TD-23 — bkz. utils/apiResult.js: GET okumaları reject etmez, sinyal res.data.status.
  const [loadFailed, setLoadFailed] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    const params = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : { pageNumber: 1, pageSize: 10 };
    setFetching(true);
    api.get(`/v1/succession?${new URLSearchParams(params)}`)
      .then((res) => {
        const { ok, items, pagination: page } = readPage(res);
        setLoadFailed(!ok);
        setData(items);
        setPagination(page);
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  // TD-23 — başarısız okumada açıkça null (bkz. utils/apiResult.js).
  const getById = useCallback(async (id) => {
    const res = await api.get(`/v1/succession/${id}`);
    return readItem(res);
  }, []);

  const createPosition = useCallback(async (payload) => {
    setFetching(true);
    try {
      await api.post('/v1/succession', payload);
      setRefreshTrigger((prev) => prev + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const updatePosition = useCallback(async (id, payload) => {
    setFetching(true);
    try {
      await api.put(`/v1/succession/${id}`, payload);
      setRefreshTrigger((prev) => prev + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const deletePosition = useCallback(async (id) => {
    setFetching(true);
    try {
      await api.delete(`/v1/succession/${id}`);
      setRefreshTrigger((prev) => prev + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const addCandidate = useCallback(async (positionId, payload) => {
    try {
      await api.post(`/v1/succession/${positionId}/candidates`, payload);
      return true;
    } catch {
      return false;
    }
  }, []);

  const updateCandidate = useCallback(async (candidateId, payload) => {
    try {
      await api.put(`/v1/succession/candidates/${candidateId}`, payload);
      return true;
    } catch {
      return false;
    }
  }, []);

  const removeCandidate = useCallback(async (candidateId) => {
    try {
      await api.delete(`/v1/succession/candidates/${candidateId}`);
      return true;
    } catch {
      return false;
    }
  }, []);

  // Task 22 — readiness token'ları backend'de yeniden tanımlandı: ready_6m|ready_1y|ready_2y
  // (eski ready_now|one_year|two_plus_years|under_review artık backend tarafından reddedilir).
  const readinessOptions = useMemo(() => [
    { id: 'ready_6m', name: t('performance.succession.readiness.ready_6m') },
    { id: 'ready_1y', name: t('performance.succession.readiness.ready_1y') },
    { id: 'ready_2y', name: t('performance.succession.readiness.ready_2y') },
  ], [t]);

  // -----------------------------------------------------------------------
  // Task 35 — durum makinesi (SuccessionStateMachine.CanTransition ile birebir):
  // draft→active / review_needed→active / active→filled / active→closed. active→
  // review_needed kasıtlı olarak BURADA YOK — o geçiş yalnız periyodik tarama
  // (RunReviewSweepAsync) tarafından otomatik yapılır, kullanıcı elle tetiklemez.
  //
  // TD-27 (Faz 2, 2026-09-11) — filled/closed ARTIK ÇIKMAZ DEĞİL: ikisinden de
  // "Yeniden Aç" (→ active) geçişi var. Sehven kapatılan pozisyon ya da halefi
  // ayrılmış "dolduruldu" kaydı, tüm aday/hazırlık geçmişini silip yeniden
  // yaratmadan izlemeye döndürülebiliyor. Liste BE'nin izin verdiği kümeyle
  // birebir kalmalı — ayrışırsa buton görünür ama uç 400 döner.
  // -----------------------------------------------------------------------
  const STATUS_TRANSITIONS = {
    draft: ['active'],
    active: ['filled', 'closed'],
    review_needed: ['active'],
    filled: ['active'],
    closed: ['active'],
  };

  const statusOptions = useMemo(() => [
    { id: 'draft', name: t('performance.succession.status.draft') },
    { id: 'active', name: t('performance.succession.status.active') },
    { id: 'review_needed', name: t('performance.succession.status.review_needed') },
    { id: 'filled', name: t('performance.succession.status.filled') },
    { id: 'closed', name: t('performance.succession.status.closed') },
  ], [t]);

  const transitionPosition = useCallback(async (id, status) => {
    try {
      await api.put(`/v1/succession/${id}/status`, { status });
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  // -----------------------------------------------------------------------
  // Task 22 — SuccessionCandidateAction (aday alt-listesi)
  // -----------------------------------------------------------------------
  // TD-23 — başarısızlıkta null döner (boş liste DEĞİL): çağıran "aksiyon yok" ile
  // "aksiyonlar yüklenemedi"yi ayırabilsin.
  const listCandidateActions = useCallback(async (candidateId) => {
    const res = await api.get(`/v1/succession/candidates/${candidateId}/actions`);
    return readItem(res);
  }, []);

  const addCandidateAction = useCallback(async (candidateId, payload) => {
    try {
      await api.post(`/v1/succession/candidates/${candidateId}/actions`, payload);
      return true;
    } catch {
      return false;
    }
  }, []);

  const updateCandidateAction = useCallback(async (actionId, payload) => {
    try {
      await api.put(`/v1/succession/actions/${actionId}`, payload);
      return true;
    } catch {
      return false;
    }
  }, []);

  const removeCandidateAction = useCallback(async (actionId) => {
    try {
      await api.delete(`/v1/succession/actions/${actionId}`);
      return true;
    } catch {
      return false;
    }
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'title',
      label: t('performance.succession.positionTitle'),
      options: {
        filter: true, sort: true,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
      },
    },
    {
      name: 'candidateCount',
      label: t('performance.succession.candidates'),
      options: { filter: false, sort: true },
    },
    {
      name: 'riskNote',
      label: t('performance.succession.riskNote'),
      options: { filter: false, sort: false },
    },
    {
      name: 'status',
      label: t('performance.succession.statusLabel'),
      options: {
        filter: true, sort: false, filterType: 'custom',
        customBodyRender: (v) => {
          const opt = statusOptions.find((o) => o.id === v);
          return opt ? opt.name : (v || '-');
        },
        filterOptions: {
          defaultOp: 'eq',
          display: (fl, onChange, i, col) => (
            <FormControl variant="standard" fullWidth>
              <InputLabel>{t('performance.succession.statusLabel')}</InputLabel>
              <Select
                value={fl[i][0] || ''}
                onChange={(e) => { fl[i] = e.target.value ? [e.target.value] : []; onChange(fl[i], i, col); }}
              >
                <MenuItem value=""><em>-</em></MenuItem>
                {statusOptions.map((o) => (<MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>))}
              </Select>
            </FormControl>
          ),
        },
      },
    },
    {
      name: 'hasNoCandidate',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (v) => {
          if (!v) return null;
          return (
            <span style={{
              background: '#FEE2E2',
              color: '#DC2626',
              borderRadius: 4,
              padding: '2px 8px',
              fontSize: 12,
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}>
              {t('performance.succession.noCandidate')}
            </span>
          );
        },
      },
    },
  ], [t, statusOptions]);

  return {
    fetching,
    data,
    pagination,
    loadFailed,
    columns,
    refresh,
    getById,
    createPosition,
    updatePosition,
    deletePosition,
    addCandidate,
    updateCandidate,
    removeCandidate,
    readinessOptions,
    statusOptions,
    STATUS_TRANSITIONS,
    transitionPosition,
    listCandidateActions,
    addCandidateAction,
    updateCandidateAction,
    removeCandidateAction,
  };
};

export default useSuccessionApi;
